import React, { useEffect, useRef } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { useInterview } from "../hooks/useInterview";
import { useSharedAudioStream } from "../hooks/useSharedAudioStream";
import { useAudioProcessor } from "../hooks/useAudioProcessor";
import { useSocketListeners } from "../hooks/useSocketListeners";
import { apiService } from "../services/apiService";
import { socketService } from "../services/socketService";
import { getFeedbackCopy } from "../utils/feedbackLocalization";
import { getUiCopy } from "../i18n/copy";
import { resolveInterviewLanguage } from "../i18n/locale";
import { useUiCopy } from "../i18n/useUiCopy";
import { scrollToBottom, formatJobDescription } from "../utils/helpers";
import { useInterviewStore, useWebSocketStore } from "../stores";

import Header from "./Header";
import ChatMessage from "./ChatMessage";
import BooleanAnswerButtons from "./BooleanAnswerButtons";
import LoadingScreen from "./LoadingScreen";
import StatusSkeleton from "./StatusSkeleton";
import JobDescriptionModal from "./JobDescriptionModal";
import WarmIntroPanel from "./WarmIntroPanel";
import { VoiceInput } from "./VoiceInput/VoiceInput";
import { VoiceListeningState } from "./VoiceListeningState/VoiceListeningState";
import { VoiceOutput } from "./VoiceOutput";
import styles from "../styles/Interview.module.css";

const InterviewApp: React.FC = () => {
  const copy = useUiCopy();
  // Use the Zustand-based state management (now the default)
  const {
    // Enhanced state management from Zustand stores
    progressPhase,
    ui,
    messages,
    session,
    applicationData,

    // Enhanced actions from combined stores
    addMessage,
    resetInterview,
    updateUI,
    startProcessing,
    stopProcessing,
    handleInterviewEvent,
    handleApplicationValidation,
    handleBooleanAnswer,
    handleUserInput,
    updateAnalysis,
    setJobDescription,
    updateSession,

    // Validators
    canSubmitInput,

    // Salary functionality (enhanced) - keeping for future use
    salaryValidationErrors,
    isSalaryQuestion,
  } = useInterview();

  const showReferralForm = useInterviewStore((state) => state.ui.showReferralForm);
  const connectionStatus = useWebSocketStore((state) => state.connectionStatus);
  const reconnectAttempts = useWebSocketStore((state) => state.reconnectAttempts);

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const [jobTitle, setJobTitle] = React.useState("");
  const [feedbackText, setFeedbackText] = React.useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = React.useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = React.useState(false);
  const showFeedbackForm = useInterviewStore((state) => state.ui.showFeedbackForm);
  const [toasts, setToasts] = React.useState<
    { id: string; text: string; level?: "success" | "warning" | "error" | "info" }[]
  >([]);
  // moved into WarmIntroPanel component
  const hasInitialized = useRef(false); // Lock to prevent Strict Mode double-invocation

  // --- Guardrails Test Diagnostics (shown on /test-guardrails) ---
  const [lastQuestionTs, setLastQuestionTs] = React.useState<number | null>(null);
  const [lastAnswerStats, setLastAnswerStats] = React.useState<{
    length: number;
    latencySec: number;
    cps: number;
  } | null>(null);
  const [timingTriggered, setTimingTriggered] = React.useState<boolean>(false);
  const [evidencePrompted] = React.useState<boolean>(false);
  const [coveragePrompted] = React.useState<boolean>(false);
  const [recentStatuses, setRecentStatuses] = React.useState<string[]>([]);
  const [voiceInputState, setVoiceInputState] = React.useState({
    isListening: false,
    isProcessingPunctuation: false,
  });
  // Track input method state for comprehensive usage tracking
  const [inputSource, setInputSource] = React.useState<"voice" | "typed" | null>(null);
  const [hasBeenEdited, setHasBeenEdited] = React.useState(false);
  const [hasUsedVoice, setHasUsedVoice] = React.useState(false);
  const [hasUsedTyping, setHasUsedTyping] = React.useState(false);
  const { sharedAudioStream, initializeSharedAudioStream } = useSharedAudioStream(voiceInputState);

  const {
    audioEnabled,
    isAudioPlaying,
    isAudioLoading,
    userHasInteracted,
    audioRef,
    processAudioChunk,
    enableAudioPlayback,
    stopCurrentPlayback,
    handleAudioEnabledChange,
    handleAudioEnded,
    handleAudioError,
  } = useAudioProcessor();

  useSocketListeners({
    audioEnabled,
    processAudioChunk,
    addMessage,
    handleInterviewEvent,
    updateUI,
    stopProcessing,
    updateAnalysis,
    setLastQuestionTs,
    setRecentStatuses,
  });

  // Wrapper for handleAudioEnabledChange that provides messages
  const handleAudioEnabledChangeWrapper = React.useCallback(
    (enabled: boolean) => {
      handleAudioEnabledChange(enabled, messages);
    },
    [handleAudioEnabledChange, messages]
  );

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      scrollToBottom(chatContainerRef.current);
    }
  }, [messages]);

  // Effect to fetch job title when application data is available
  // NOTE: We now prefer job_title from validation response; only fall back to API if needed.
  useEffect(() => {
    const jobId = applicationData.job_id;
    if (jobId) {
      // If jobTitle and description already set from validation enrichment, skip the extra call
      if (!jobTitle || !session.jobDescription) {
        // ✅ FIX: Added .catch() to prevent unhandled promise rejection (JAVASCRIPT-REACT-J)
        updateJobTitle(jobId).catch((error) => {
          console.error("Failed to update job title:", error);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationData.job_id, jobTitle, session.jobDescription]);

  // --- Smart Animation Controller for Loading Progress ---
  // Smoothly animates progress bar to match backend updates with adaptive speed
  useEffect(() => {
    let animationFrameId: number = 0;
    let creepIntervalId: NodeJS.Timeout | null = null;
    let lastTarget = 0;

    const animate = () => {
      const { visualLoadingProgress, loadingProgress } = useInterviewStore.getState().ui;
      const current = visualLoadingProgress ?? 0;
      const target = loadingProgress;
      
      // Detect when target changes (new backend update)
      const targetChanged = target !== lastTarget;
      if (targetChanged) {
        lastTarget = target;
        // Clear creep animation when new target arrives
        if (creepIntervalId) {
          clearInterval(creepIntervalId);
          creepIntervalId = null;
        }
      }

      // If we're at or past the target, stop animating
      if (current >= target) {
        // Only start creep if we're waiting at target (not at 100%)
        if (target < 100 && !creepIntervalId) {
          creepIntervalId = setInterval(() => {
            const currentVisual = useInterviewStore.getState().ui.visualLoadingProgress ?? 0;
            const currentTarget = useInterviewStore.getState().ui.loadingProgress;
            // Stop creep if target changed or we're at 100%
            if (currentTarget !== target || currentVisual >= 98) {
              if (creepIntervalId) {
                clearInterval(creepIntervalId);
                creepIntervalId = null;
              }
              return;
            }
            // Slowly creep forward while waiting (max 2% above target)
            if (currentVisual < Math.min(target + 2, 98)) {
              useInterviewStore.getState().setVisualLoadingProgress(currentVisual + 0.05);
            }
          }, 1500); // Creep slowly (every 1.5s)
        }
        return;
      }

      // Calculate adaptive animation speed based on distance to target
      const distance = target - current;
      let increment: number;

      if (distance > 50) {
        // Large gap: fast animation (e.g., jumping from 30% to 90%)
        increment = Math.min(distance * 0.15, 3.0); // Up to 3% per frame
      } else if (distance > 20) {
        // Medium gap: moderate speed
        increment = Math.min(distance * 0.12, 1.5); // Up to 1.5% per frame
      } else if (distance > 5) {
        // Small gap: smooth approach
        increment = Math.min(distance * 0.1, 0.8); // Up to 0.8% per frame
      } else {
        // Very close: fine-tune
        increment = Math.min(distance * 0.15, 0.3); // Up to 0.3% per frame
      }

      const newProgress = Math.min(current + increment, target);
      
      // Update progress and continue animation
      if (newProgress > current) {
        useInterviewStore.getState().setVisualLoadingProgress(newProgress);
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    if (ui.showOverlay) {
      // When overlay appears or target progress changes, restart animation
      cancelAnimationFrame(animationFrameId);
      if (creepIntervalId) {
        clearInterval(creepIntervalId);
        creepIntervalId = null;
      }
      lastTarget = ui.loadingProgress;
      animationFrameId = requestAnimationFrame(animate);
    }

    return () => {
      // Cleanup on unmount
      cancelAnimationFrame(animationFrameId);
      if (creepIntervalId) {
        clearInterval(creepIntervalId);
      }
    };
  }, [ui.loadingProgress, ui.showOverlay]);

  // Initialize app and check for URL parameters
  useEffect(() => {
    // In development, React's StrictMode calls this effect twice.
    // This ref lock ensures the initialization logic only runs once.
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    // Connect to WebSocket on initial load
    // Fire-and-forget connect attempt must ALWAYS handle rejection to avoid
    // global `unhandledrejection` noise in Sentry.
    void useWebSocketStore
      .getState()
      .connect()
      .catch((error) => {
        console.error("[Init] WebSocket connect failed:", error);
      });

    const initializeApp = async () => {
      // Immediately show a generic loading screen to prevent any component flashing.
      updateUI({
        showInputArea: false,
        showOverlay: true,
        overlayMessage: getUiCopy(useInterviewStore.getState().session.uiLocale).initializing,
        loadingProgress: 5,
        showLoadingIndicator: false,
      });

      // A brief, intentional pause to make the "Initializing" state feel deliberate.
      await new Promise((resolve) => setTimeout(resolve, 500));

      const urlParams = new URLSearchParams(window.location.search);
      const debugReferral = urlParams.get("debug_referral");
      const debugFeedback = urlParams.get("debug_feedback");
      const debugRating = urlParams.get("debug_rating");
      const debugLanguage = urlParams.get("debug_language") || "English";
      if (debugReferral === "1" && process.env.NODE_ENV !== "production") {
        resetInterview();
        updateUI({
          showOverlay: false,
          overlayMessage: "",
          loadingProgress: 0,
          showInputArea: false,
          showBooleanButtons: false,
          showReferralForm: true,
        });
        setJobTitle((prev) => prev || "Referral Preview");
        return;
      }
      if (debugFeedback === "1" && process.env.NODE_ENV !== "production") {
        resetInterview();
        updateUI({
          showOverlay: false,
          overlayMessage: "",
          loadingProgress: 0,
          showInputArea: false,
          showBooleanButtons: false,
          showReferralForm: false,
          showFeedbackForm: true,
        });
        setJobTitle((prev) => prev || "Feedback Preview");
        return;
      }
      if (debugRating === "1" && process.env.NODE_ENV !== "production") {
        const feedbackCopy = getFeedbackCopy(debugLanguage);
        const resolvedLanguage = resolveInterviewLanguage({ interview_language: debugLanguage });
        resetInterview();
        updateSession({
          interviewLanguage: resolvedLanguage.interviewLanguage,
          uiLocale: resolvedLanguage.uiLocale,
          recognitionLocale: resolvedLanguage.recognitionLocale,
          htmlLang: resolvedLanguage.htmlLang,
        });
        addMessage(
          feedbackCopy.prompt,
          "ai",
          false,
          false,
          undefined,
          "complete",
          true
        );
        updateUI({
          showOverlay: false,
          overlayMessage: "",
          loadingProgress: 0,
          showInputArea: false,
          showBooleanButtons: false,
          showFeedbackForm: false,
        });
        setJobTitle((prev) => prev || "Rating Preview");
        return;
      }

      const appIdFromUrl = urlParams.get("app_id") || urlParams.get("id");

      if (appIdFromUrl) {
        // App ID exists, so we transition the loading message and proceed with validation.
        updateUI({
          showOverlay: true,
          overlayMessage: getUiCopy(useInterviewStore.getState().session.uiLocale).validatingApplicationId,
          loadingProgress: 10,
        });
        await validateApplicationId(appIdFromUrl);
      } else {
        // No App ID, so hide the overlay and show the error message.
        resetInterview();
        const missingCopy = getUiCopy(useInterviewStore.getState().session.uiLocale);
        addMessage(
          missingCopy.missingApplicationId,
          "ai",
          true,
          false,
          "error"
        );
        updateUI({
          showOverlay: false, // Hide loading overlay
          inputPlaceholder: missingCopy.applicationIdMissingPlaceholder,
          sendButtonText: missingCopy.unavailable,
          sendButtonDisabled: true,
          inputDisabled: true,
          showInputArea: true, // Show the disabled input area
        });
      }
    };

    initializeApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array is correct for initialization

  // Dev-only keyboard shortcut: Cmd/Ctrl+Shift+R toggles referral panel
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    const handler = (e: KeyboardEvent) => {
      const isCmd = navigator.platform.toUpperCase().includes("MAC") ? e.metaKey : e.ctrlKey;
      if (isCmd && e.shiftKey && (e.key === "R" || e.key === "r")) {
        e.preventDefault();
        const state = useInterviewStore.getState();
        state.updateUI({
          showReferralForm: !state.ui.showReferralForm,
          showInputArea: false,
          showBooleanButtons: false,
        });
        if (!jobTitle) setJobTitle("Referral Preview");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [jobTitle]);

  // Application validation with enhanced error handling
  const validateApplicationId = async (appId: string) => {
    try {
      handleInterviewEvent("VALIDATION_SUCCESS");
      startProcessing(getUiCopy(useInterviewStore.getState().session.uiLocale).validatingApplicationId);

      const response = await apiService.validateApplicationId(appId);

      if (response.status === "success") {
        // Immediately set job title and description from validation enrichment if present
        if (response.job) {
          const title =
            response.job.TR1__External_Job_Title__c ||
            response.job.TR1__Job_Title__c ||
            getUiCopy(useInterviewStore.getState().session.uiLocale).position;
          setJobTitle(title);

          const rawDescription =
            response.job.Standardized_Job_Description__c ||
            getUiCopy(useInterviewStore.getState().session.uiLocale).noDescription;
          const formattedDescription = formatJobDescription(rawDescription);
          setJobDescription(formattedDescription);
          const resolvedLanguage = resolveInterviewLanguage({
            TH_Job_Language__c: response.job.TH_Job_Language__c,
            Interview_Language__c: response.job.Interview_Language__c,
            interview_language: response.interview_language,
          });
          updateSession({
            interviewLanguage: resolvedLanguage.interviewLanguage,
            uiLocale: resolvedLanguage.uiLocale,
            recognitionLocale: resolvedLanguage.recognitionLocale,
            htmlLang: resolvedLanguage.htmlLang,
          });
        } else if (response.job_title) {
          setJobTitle(response.job_title);
        }

        await handleApplicationValidation({
          application_id: appId,
          job_id: response.job_id!,
          applicant_id: response.applicant_id!,
          applicant_email: response.applicant_email,
          opportunity_discussed_id: response.opportunity_discussed_id,
        });
      } else {
        console.error("ISA Interviewer: Validation failed:", response.message || "Unknown error");
        addMessage(
          getUiCopy(useInterviewStore.getState().session.uiLocale).validationFailed(
            response.message || "Application ID validation failed"
          ),
          "status",
          false,
          false,
          "error"
        );
        handleInterviewEvent("ERROR_OCCURRED");
      }
    } catch (error) {
      console.error("ISA Interviewer: Validation error:", error);
      addMessage(
        getUiCopy(useInterviewStore.getState().session.uiLocale).validationFailedConnection,
        "status",
        false,
        false,
        "error"
      );
      handleInterviewEvent("ERROR_OCCURRED");
    } finally {
      stopProcessing();
    }
  };

  // Job title fetching
  const updateJobTitle = React.useCallback(
    async (jobId: string) => {
      if (jobId) {
        try {
          const response = await apiService.getJobDetails(jobId);
          if (response.status === "success" && response.job) {
            const title =
              response.job.TR1__External_Job_Title__c ||
              response.job.TR1__Job_Title__c ||
              getUiCopy(useInterviewStore.getState().session.uiLocale).position;
            setJobTitle(title);
            const rawDescription =
              response.job.Standardized_Job_Description__c ||
              getUiCopy(useInterviewStore.getState().session.uiLocale).noDescription;
            const formattedDescription = formatJobDescription(rawDescription);
            setJobDescription(formattedDescription);
            const jobRecord = response.job as typeof response.job & {
              TH_Job_Language__c?: unknown;
              Interview_Language__c?: unknown;
            };
            if (jobRecord.TH_Job_Language__c || jobRecord.Interview_Language__c) {
              const resolvedLanguage = resolveInterviewLanguage(jobRecord);
              updateSession({
                interviewLanguage: resolvedLanguage.interviewLanguage,
                uiLocale: resolvedLanguage.uiLocale,
                recognitionLocale: resolvedLanguage.recognitionLocale,
                htmlLang: resolvedLanguage.htmlLang,
              });
            }
          }
        } catch (error) {
          console.error("Failed to fetch job title:", error);
        }
      }
    },
    [setJobDescription, updateSession]
  );

  const handleSubmit = () => {
    const { inputValue } = ui;

    if (!canSubmitInput || session.isProcessing || connectionStatus !== "connected") {
      return;
    }

    // Stop any currently playing audio when submitting a new message
    // but keep audio enabled for the new response
    if (audioEnabled && isAudioPlaying) {
      stopCurrentPlayback();
    }

    // Compute simple timing stats for diagnostics
    if (lastQuestionTs) {
      const latencySec = Math.max(0, (Date.now() - lastQuestionTs) / 1000);
      const length = (inputValue || "").length;
      const cps = latencySec > 0 ? length / latencySec : 0;
      setLastAnswerStats({ length, latencySec, cps });
      // Heuristic mirrors backend guard thresholds (frontend-only signal)
      if (length >= 300 && cps >= 25) {
        setTimingTriggered(true);
      } else {
        setTimingTriggered(false);
      }
    }

    // Determine input method based on state
    let inputMethod: string;
    if (!hasUsedVoice) {
      // Pure typing, voice never used
      inputMethod = "typed";
    } else if (!hasUsedTyping) {
      // Pure voice, typing never used (typing only used for edits, which we track separately)
      if (!hasBeenEdited) {
        inputMethod = "voice";
      } else {
        inputMethod = "voice_edited";
      }
    } else {
      // Both voice and typing were used - need to determine order
      // If current source is voice and not edited, typing happened first
      if (inputSource === "voice" && !hasBeenEdited) {
        // Typed first, then switched to voice (no edits after voice)
        inputMethod = "typed_voice";
      } else {
        // Either: typed first then voice then edited, OR voice first then typed (edited)
        // Both cases result in mixed input with edits
        inputMethod = "typed_voice_edited";
      }
    }

    handleUserInput(inputValue, inputMethod);

    // Reset input tracking state and clear input
    setInputSource(null);
    setHasBeenEdited(false);
    setHasUsedVoice(false);
    setHasUsedTyping(false);
    updateUI({ inputValue: "" });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    updateUI({
      inputValue: value,
      sendButtonDisabled: value.trim().length === 0,
    });

    // Track input source and edit state
    setHasUsedTyping(true); // Mark that typing has been used
    
    if (inputSource === "voice" || inputSource === null) {
      // User is typing (either first input or after voice)
      setInputSource("typed");
      if (hasUsedVoice) {
        // If voice was used before, mark as edited
        setHasBeenEdited(true);
      }
    } else if (inputSource === "typed") {
      // Already typed, just mark as edited if voice was used
      if (hasUsedVoice) {
        setHasBeenEdited(true);
      }
    }
  };

  const handleVoiceReplace = React.useCallback(
    (voiceText: string) => {
      // Replace entire input value with the punctuated text
      updateUI({
        inputValue: voiceText,
        sendButtonDisabled: voiceText.trim().length === 0,
      });

      // Mark that voice was used and is the current source
      setHasUsedVoice(true);
      setInputSource("voice");
      setHasBeenEdited(false); // Reset edit flag when new voice input arrives

      // Focus the textarea after voice input
      if (chatInputRef.current) {
        chatInputRef.current.focus();
      }
    },
    [updateUI]
  );

  const handleVoiceStateChange = React.useCallback(
    (state: { isListening: boolean; isProcessingPunctuation: boolean }) => {
      setVoiceInputState(state);

      // If voice input starts listening, stop audio playback but keep audioEnabled true
      if (state.isListening && audioEnabled && isAudioPlaying) {
        stopCurrentPlayback();
      }
    },
    [audioEnabled, isAudioPlaying, stopCurrentPlayback]
  );
  const isVoiceInputDisabled = React.useMemo(() => {
    return ui.inputDisabled || session.isProcessing || connectionStatus !== "connected";
  }, [ui.inputDisabled, session.isProcessing, connectionStatus]);

  const handleBooleanAnswerClick = (answer: boolean) => {
    handleBooleanAnswer(answer);
  };

  const renderConnectionStatus = () => {
    if (connectionStatus === "reconnecting") {
      return (
        <div
          className={styles.connectionStatusBanner}
          style={{ backgroundColor: "#ffc107", color: "#212529" }}
        >
          {copy.connectionLost(reconnectAttempts)}
        </div>
      );
    }
    if (connectionStatus === "failed") {
      return (
        <div
          className={styles.connectionStatusBanner}
          style={{ backgroundColor: "#dc3545", color: "white" }}
        >
          {copy.connectionFailed}
        </div>
      );
    }
    return null;
  };

  // The new loading screen is now the primary overlay
  const renderLoadingOverlay = () => {
    return (
      <LoadingScreen
        visible={ui.showOverlay}
        message={ui.overlayMessage}
        progress={ui.visualLoadingProgress ?? ui.loadingProgress}
        jobTitle={jobTitle}
      />
    );
  };

  const showToast = React.useCallback(
    (text: string, level?: "success" | "warning" | "error" | "info") => {
      const id = Math.random().toString(36).slice(2);
      setToasts((prev) => [...prev, { id, text, level }]);
      // Auto-dismiss after 2.5 seconds
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 2500);
    },
    []
  );

  // replace inline warm intro with reusable component

  // Show salary validation errors if any
  const renderSalaryValidation = () => {
    if (!salaryValidationErrors || salaryValidationErrors.length === 0) {
      return null;
    }
    return (
      <div
        id="validation-errors"
        className={styles.salaryValidation}
        role="alert"
        aria-live="polite"
      >
        {salaryValidationErrors.map((error, index) => (
          <p key={index} className={styles.errorMessage}>
            {error}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.interviewContainer}>
      {/* Hidden audio element for TTS playback */}
      <audio
        ref={audioRef}
        style={{ display: "none" }}
        onEnded={handleAudioEnded}
        onError={handleAudioError}
      />

      {/* Voice quickstart removed from main UI; use /?voice-test=true for the isolated test page */}
      <JobDescriptionModal />
      {renderLoadingOverlay()}
      <Header jobTitle={jobTitle} currentPhase={progressPhase} />

      {/* Guardrails diagnostics panel: visible on /test-guardrails */}
      {window.location.pathname.includes("/test-guardrails") && (
        <div
          style={{
            background: "#0b1020",
            color: "#dce1ff",
            padding: "8px 12px",
            border: "1px solid #223",
            borderRadius: 6,
            margin: "8px 12px",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Guardrails Diagnostics</div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
            <div>
              <div>Timing</div>
              <div>Triggered: {timingTriggered ? "yes" : "no"}</div>
              <div>
                {lastAnswerStats
                  ? `len=${lastAnswerStats.length}, latency=${lastAnswerStats.latencySec.toFixed(
                      2
                    )}s, cps=${lastAnswerStats.cps.toFixed(1)}`
                  : "n/a"}
              </div>
            </div>
            <div>
              <div>Resume Evidence</div>
              <div>Prompted: {evidencePrompted ? "yes" : "no"}</div>
            </div>
            <div>
              <div>JD Coverage</div>
              <div>Prompted: {coveragePrompted ? "yes" : "no"}</div>
            </div>
            <div>
              <div>Recent Status</div>
              <div style={{ maxWidth: 420 }}>
                {recentStatuses.map((s, i) => (
                  <div key={i} style={{ opacity: 0.8 }}>
                    • {s}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Toasts */}
      {toasts.length > 0 && (
        <div className={styles.toastContainer} aria-live="polite" aria-atomic="true">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`${styles.toast} ${t.level ? styles[t.level] : ""}`}
              role="status"
            >
              {t.text}
            </div>
          ))}
        </div>
      )}
      {renderConnectionStatus()}
      <div className={styles.chatContainer} ref={chatContainerRef}>
        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} />
        ))}
        {showReferralForm && (
          <>
            <ChatMessage
              message={{
                id: "referral-intro-message",
                type: "ai",
                content: copy.referralPrompt,
                timestamp: new Date(),
                is_html: false,
                is_referral: true,
              }}
            />
            <div className={styles.referralImageWrap}>
              <div className={styles.referralImageFrame}>
                <img
                  src={process.env.PUBLIC_URL + "/ISA Smiling Medium.jpeg"}
                  alt="ISA smiling"
                  className={styles.referralImage}
                  loading="lazy"
                />
              </div>
            </div>
            <WarmIntroPanel
              applicantId={applicationData.applicant_id}
              opportunityDiscussedId={applicationData.opportunity_discussed_id}
              jobTitle={jobTitle}
              onToast={(text, level) => showToast(text, level)}
            />
          </>
        )}
        {showFeedbackForm && (
          <div
            className={`${styles.inputArea} ${styles.feedbackContainer}`}
            style={{ position: "relative" }}
          >
            <div className={styles.feedbackHeader}>
              <div className={styles.feedbackLabel}>
                {copy.feedbackTitle}
                <span className={styles.tooltip}>
                  <span className={styles.tooltipIcon}>i</span>
                  <span className={styles.tooltipContent}>
                    {copy.feedbackTooltip}
                  </span>
                </span>
              </div>
              <div className={styles.feedbackRight}></div>
            </div>
            <div className={styles.charCounterFloat}>{feedbackText.length} / 4000</div>
            <div className={styles.inputWrapper}>
              <TextareaAutosize
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder={copy.feedbackPlaceholder}
                className={styles.chatInput}
                minRows={3}
                maxRows={8}
                disabled={
                  isSubmittingFeedback ||
                  useWebSocketStore.getState().connectionStatus !== "connected"
                }
                style={{ resize: "none" }}
                maxLength={4000}
                aria-label={copy.feedbackInputLabel}
                aria-required="true"
                aria-multiline="true"
                role="textbox"
              />
              <button
                onClick={() => {
                  const text = feedbackText.trim();
                  setIsSubmittingFeedback(true);
                  try {
                    socketService.submitFeedback(text);
                    handleInterviewEvent("FEEDBACK_SUBMITTED");
                    updateUI({
                      showFeedbackForm: false,
                      showInputArea: false,
                      showBooleanButtons: false,
                    });
                    setFeedbackSubmitted(true);
                  } finally {
                    setIsSubmittingFeedback(false);
                  }
                }}
                disabled={
                  isSubmittingFeedback ||
                  feedbackSubmitted ||
                  feedbackText.length > 4000 ||
                  useWebSocketStore.getState().connectionStatus !== "connected"
                }
                className={styles.sendButton}
              >
                {feedbackSubmitted ? copy.submitted : copy.submit}
              </button>
            </div>
            {feedbackSubmitted && (
              <div className={styles.successBottomLeft}>{copy.thanksFeedback}</div>
            )}
          </div>
        )}
        {ui.currentSkeleton && <StatusSkeleton variant={ui.currentSkeleton} />}
        {ui.showBooleanButtons && (
          <BooleanAnswerButtons
            onAnswer={handleBooleanAnswerClick}
            disabled={ui.booleanAnswerGiven}
          />
        )}
      </div>
      <div className={styles.footer}>
        {ui.showInputArea && (
          <div className={styles.inputArea}>
            <div className={styles.inputWrapper}>
              {(voiceInputState.isListening || voiceInputState.isProcessingPunctuation) && (
                <VoiceListeningState
                  voiceInputState={voiceInputState}
                  audioStream={sharedAudioStream}
                />
              )}
              <TextareaAutosize
                ref={chatInputRef}
                value={ui.inputValue}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder={ui.inputPlaceholder}
                className={styles.chatInput}
                minRows={3}
                maxRows={8}
                disabled={
                  ui.inputDisabled ||
                  session.isProcessing ||
                  useWebSocketStore.getState().connectionStatus !== "connected" ||
                  voiceInputState.isListening ||
                  voiceInputState.isProcessingPunctuation
                }
                style={{ resize: "none" }}
                maxLength={isSalaryQuestion ? 255 : undefined}
                aria-label={copy.interviewResponseInput}
                aria-describedby={
                  `${isSalaryQuestion ? "char-counter " : ""}${
                    salaryValidationErrors?.length ? "validation-errors" : ""
                  }`.trim() || undefined
                }
                aria-invalid={salaryValidationErrors?.length ? "true" : "false"}
                aria-required="true"
                aria-multiline="true"
                role="textbox"
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 8,
                }}
              >
                <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <VoiceInput
                    onTextReplace={handleVoiceReplace}
                    currentText={ui.inputValue}
                    disabled={isVoiceInputDisabled}
                    recognitionLanguage={session.recognitionLocale || session.interviewLanguage}
                    onStateChange={handleVoiceStateChange}
                    onStreamInitialize={initializeSharedAudioStream}
                  />
                  <VoiceOutput
                    audioEnabled={audioEnabled}
                    isAudioLoading={isAudioLoading}
                    userHasInteracted={userHasInteracted}
                    onEnableAudioPlayback={enableAudioPlayback}
                    onAudioEnabledChange={handleAudioEnabledChangeWrapper}
                    onProcessAudioChunk={processAudioChunk}
                  />
                </div>
                <button
                  aria-label={copy.submitAnswer}
                  onClick={handleSubmit}
                  disabled={
                    !canSubmitInput ||
                    useWebSocketStore.getState().connectionStatus !== "connected" ||
                    voiceInputState.isListening ||
                    voiceInputState.isProcessingPunctuation
                  }
                  className={styles.sendButton}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m22 2-7 20-4-9-9-4Z" />
                    <path d="M22 2 11 13" />
                  </svg>
                </button>
              </div>
            </div>
            {isSalaryQuestion && (
              <div id="char-counter" className={styles.charCounter}>
                <span className={ui.inputValue.length > 255 ? styles.errorText : ""}>
                  {ui.inputValue.length} / 255
                </span>
              </div>
            )}
            {renderSalaryValidation()}
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewApp;
